import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import firebase from 'firebase/app';
import 'firebase/auth';
import { environment } from '../environments/environment';

@Injectable()
export class AuthTokenInterceptor implements HttpInterceptor {
  constructor() {}

  private static readonly SKIP_AUTH_URLS = [
    'auth-checkAccountLockout',
    'auth-recordLoginAttempt',
    'auth-preSignIn',
    'invitations-join',
  ];

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip JSONP requests — they don't support HTTP headers
    if (req.method === 'JSONP') {
      return next.handle(req);
    }

    // Skip auth for pre-authentication endpoints (called before user is logged in)
    if (AuthTokenInterceptor.SKIP_AUTH_URLS.some(url => req.url.includes(url))) {
      return next.handle(req);
    }

    return this.getToken(false).pipe(
      switchMap(token => {
        let headersConfig: { [key: string]: string } = {};

        if (token) {
          headersConfig['Authorization'] = `Bearer ${token}`;
        } else {
          console.warn('[AuthInterceptor] No token available for request:', req.url);
        }

        // Note: BFF resolves facility from user's Firestore document (facilityId field)
        // No need to send X-Facility header - BFF handles facility routing server-side
        // For dev_header mode, BFF derives facility from JWT user ID automatically

        const authReq = req.clone({
          setHeaders: headersConfig
        });
        return next.handle(authReq).pipe(
          catchError((error: HttpErrorResponse) => {
            // If we get a 401, try refreshing the token and retry once
            if (error.status === 401) {
              console.warn('[AuthInterceptor] Received 401, attempting token refresh...');
              if (token) {
                return this.getToken(true).pipe(
                  switchMap(newToken => {
                    if (newToken && newToken !== token) {
                      console.log('[AuthInterceptor] Token refreshed, retrying request');
                      const retryReq = req.clone({
                        setHeaders: {
                          'Authorization': `Bearer ${newToken}`
                        }
                      });
                      return next.handle(retryReq);
                    } else {
                      console.error('[AuthInterceptor] Token refresh failed or returned same token');
                      return throwError(() => error);
                    }
                  }),
                  catchError((refreshError) => {
                    console.error('[AuthInterceptor] Error during token refresh:', refreshError);
                    return throwError(() => error);
                  })
                );
              } else {
                console.error('[AuthInterceptor] No token available to refresh');
                return throwError(() => error);
              }
            }
            return throwError(() => error);
          })
        );
      }),
      catchError((error) => {
        console.error('[AuthInterceptor] Error getting token:', error);
        return throwError(() => error);
      })
    );
  }

  private getToken(forceRefresh: boolean = false): Observable<string | null> {
    return new Observable(observer => {
      const unsubscribe = firebase.auth().onAuthStateChanged(
        user => {
          unsubscribe();
          if (user) {
            user.getIdToken(forceRefresh).then(
              token => {
                observer.next(token);
                observer.complete();
              },
              err => {
                console.error('Error getting Firebase token:', err);
                observer.error(err);
              }
            );
          } else {
            observer.next(null);
            observer.complete();
          }
        },
        err => {
          console.error('Error in auth state change:', err);
          observer.error(err);
        }
      );
    });
  }
}
